import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useState } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import { RegionCard } from '@app/components/blocks/card/RegionCard'
import { Style, styled } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { pluralize } from '@i18n/localizeHelper'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import useMediaQuery from '@app/util/useMediaQuery'
import { pages } from '@app/util/links'
import { ListRegionModel } from 'functions/app/geography/list/region'
import { createRandomGenerator } from '@app/util/helpers'

export const ProvinceSection = ({
  searchTerm,
  onResetSearch,
}: {
  searchTerm: string
  onResetSearch: () => void
}) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app.discoveryPage
  const client = useClient()
  const [regions, setRegions] = useState<ListRegionModel[]>([])
  const [mounted, setMounted] = useState(false)

  const dataServerQuery = useQuery('list-region', {
    sortByPartnerFirst: true,
  })
  const serverRegions: ListRegionModel[] = dataServerQuery.data ?? []

  useEffect(() => {
    const close = client
      .query('list-region', {
        searchTerm: searchTerm,
        sortByPartnerFirst: true,
      })
      .subscribe((result) => {
        setRegions(result)
        setMounted(true)
      })
    return () => close()
  }, [client, searchTerm])

  const renderedRegions = mounted ? regions : serverRegions

  const gridStyle: Style = isMobile
    ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(150px, 1fr))',
        gap: '1rem',
      }
    : {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(300px, 1fr))',
        '@media (max-width: 1400px)': {
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
        },
        '@media (max-width: 1200px)': {
          gridTemplateColumns: 'repeat(2, minmax(300px, 1fr))',
        },
        gap: '1rem',
      }

  const random = getMinuteRandom()

  return (
    <Column alignItems="stretch" style={{ width: '100%' }}>
      <Column gap="0rem">
        <Title.Page styledAsLevel={3} style={{ alignSelf: 'start' }}>
          {translations.tabs.regions.provinceTitle}
        </Title.Page>
        {searchTerm ? (
          <Row style={{ flexWrap: 'wrap', gap: '1rem' }}>
            <Tag
              variant="subtle"
              size="small"
              label={searchTerm}
              iconRight={faXmark}
              onClick={onResetSearch}
            />
            <Text.Medium>
              {searchTerm
                ? pluralize(translations.searchResults, renderedRegions.length)
                : translations.tabs.regions.provinceSubTitle}
            </Text.Medium>
          </Row>
        ) : (
          <Text.Medium>
            {translations.tabs.regions.provinceSubTitle}
          </Text.Medium>
        )}
      </Column>
      <styled.div style={gridStyle}>
        {renderedRegions.map((region) => (
          <RegionCard
            id={region.id}
            key={region.code}
            title={region.name}
            to={pages.regionPage(region.slug)}
            subsidies={null}
            isCrowdbuildingPartner={region.crowdbuildingPartner}
            initiatives={region.initiatives?.count ?? 0}
            avatars={region.followers ?? []}
            colorIndex={Math.floor(random() * 4)}
          />
        ))}
      </styled.div>
    </Column>
  )
}

const getMinuteRandom = () => {
  const now = Date.now()
  const minute = Math.floor(now / (60 * 1000))
  return createRandomGenerator(minute)
}
